import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';
import {
  CREATE_FORM_URL,
  GET_FORMS_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';

function get_form_status_icon(status) {
  switch (status) {
    case 'active':
      return 'bi bi-check2-circle';
    case 'inactive':
    case 'in-active':
      return 'bi bi-file-earmark-text';
    case 'draft':
      return 'bi bi-pencil-square';
    default:
      return 'bi bi-file-earmark-text';
  }
}

function get_form_status_badge_class(status) {
  switch (status) {
    case 'active':
      return 'text-success bg-success-subtle';
    case 'inactive':
    case 'in-active':
      return 'text-secondary bg-secondary-subtle';
    case 'draft':
      return 'text-warning bg-warning-subtle';
    default:
      return 'text-primary bg-primary-subtle';
  }
}


export default {
  name: "CreateForm",
  mixins: [alert_mixin],
  data: () => ({
    active_forms: [],
    active_forms_loading: false,
    active_tab: 'forms',
    form_name: "",
    form_description: "",
    form_type_id: "",
    create_form_running: false,
    modal_instance: null,
    modal_error_message: "",
  }),
  computed: {

    all_forms() {
      return this.active_forms;
    },
    archived_history_forms() {
      return this.active_forms.filter((form) => ['inactive', 'in-active'].includes(form.status_normalized));
    },
    form_types() {
      return this.$store.getters.formTypes;
    },
    form_types_loading() {
      return this.$store.getters.formMetadataLoading;
    },
  },
  created() {
    this.load_initial_data();
  },
  methods: {
    parse_response_data(response) {
      let data = response?.data ?? response ?? {};

      for (let attempt = 0; attempt < 2 && typeof data === 'string'; attempt += 1) {
        try {
          data = JSON.parse(data);
        } catch {
          return {};
        }
      }

      return data;
    },

    extract_items(response) {
      const parsed = this.parse_response_data(response);
      const items = parsed?.data ?? parsed?.results ?? [];

      return Array.isArray(items) ? items : [];
    },

    map_form_to_card(form) {
      const status = String(form?.status ?? '').trim();
      const normalized_status = status.toLowerCase().replace(/[\s_]+/g, '-');
      const form_type = form?.form_type?.form_type || form?.form_type?.name || 'Form';
      const title = form?.form_name || 'Untitled Form';
      const description = form?.form_description || 'No description provided.';
      const last_edited = form?.date_created || 'Unknown date';

      const result = {
        id: form?.id ?? `${title}-${last_edited}`,
        title,
        description,
        last_edited,
        status: status || 'Unknown',
        status_normalized: normalized_status,
        icon: get_form_status_icon(normalized_status),
        icon_wrapper_class: '',
        status_badge_class: get_form_status_badge_class(normalized_status),
        form_type_label: form_type,
      };

      return result;
    },

    set_active_tab(tab) {
      this.active_tab = tab;
    },

    open_create_form_modal() {
      this.modal_error_message = "";
      this.form_name = "";
      this.form_description = "";
      this.form_type_id = String(this.form_types?.[0]?.id ?? '');
    },

    async load_initial_data() {
      return Promise.all([
        this.$store.dispatch("ensureFormMetadataLoaded"),
        this.load_forms(),
      ]).catch((error) => {
        console.warn("Failed to load form data", error);
      });
    },

    async load_forms() {
      this.active_forms_loading = true;

      try {
        const response = await axios.get(GET_FORMS_URL, get_request_config());
        const forms = this.extract_items(response);
        this.active_forms = Array.isArray(forms)
          ? forms.map((form) => this.map_form_to_card(form))
          : [];
      } catch (error) {
        this.active_forms = [];
      } finally {
        this.active_forms_loading = false;
      }
    },

    async create_form() {
      this.modal_error_message = "";

      if (!this.form_name.trim()) {
        this.modal_error_message = "Please enter a form name.";
        return;
      }

      if (!this.form_type_id) {
        this.modal_error_message = "Please select a form type.";
        return;
      }

      this.create_form_running = true;
      const loader = await this.show_loader("Creating form...");

      try {
        const payload = {
          form_name: this.form_name.trim(),
          form_description: this.form_description.trim(),
          form_type_id: Number(this.form_type_id),
        };

        const response = await axios.post(CREATE_FORM_URL, payload, get_request_config());
        const parsed_response = this.parse_response_data(response);

        if (parsed_response?.status === "success") {
          this.show_success(parsed_response.message || "Form created successfully.");
          this.close_modal("create_form");
          this.form_name = "";
          this.form_description = "";
          this.form_type_id = String(this.form_types?.[0]?.id ?? '');
          await this.load_forms();
          return;
        }

        this.show_error(parsed_response?.message || "Failed to create form.");
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create form.";
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.create_form_running = false;
      }
    },
  },

};
